import { useState, useRef } from "react";
import { useAsync } from "react-async-hook";
import useConstant from "use-constant";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import type { Dispatch, SetStateAction } from "react";

// Generic reusable hook
interface DebouncedSearchResult {
  inputText: string;
  setInputText: Dispatch<SetStateAction<string>>;
}

export const useDebouncedSearch = <Result>(
  searchFunction: (text: string) => Promise<Result>
): DebouncedSearchResult => {
  // Handle the input text state
  const [inputText, setInputText] = useState("");

  // Debounce the original search async function
  const debouncedSearchFunction = useConstant(() => AwesomeDebouncePromise(searchFunction, 500));

  // The async callback is run each time the text changes,
  // but as the search function is debounced, it does not
  // fire a new request on each keystroke
  // Returns empty array on first load
  const firstUpdate = useRef(true);
  useAsync(async () => {
    if (firstUpdate.current) {
      firstUpdate.current = false;

      return [];
    }

    return debouncedSearchFunction(inputText);
  }, [debouncedSearchFunction, inputText]);

  // Return everything needed for the hook consumer
  return {
    inputText,
    setInputText,
  };
};
